import { Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactElement, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { smaverseState } from '@app/domain/top-nav';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { CategoryName } from '@app/types/catalog';

const tabs = [
  { label: '住宅展示場を探す', value: CategoryName.EXHIBITION },
  { label: '分譲を探す', value: CategoryName.PROPERTY },
] as const;

interface SearchTypeTabProps {
  onChangeTab: (selectedTab: string) => void;
  value?: string | null;
}

export function SearchTypeTab({
  onChangeTab,
  value,
}: SearchTypeTabProps): ReactElement {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useDeviceType();
  const setIsSmaverse = useSetRecoilState(smaverseState);
  const selected = useMemo(() => {
    if (location.pathname.includes('/exhibitions')) {
      return CategoryName.EXHIBITION;
    } else if (location.pathname.includes('/properties')) {
      return CategoryName.PROPERTY;
    }
    return tabs[0].value;
  }, [location.pathname]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, selectedTab: string) => {
      onChangeTab(selectedTab);
      setIsSmaverse(false);
      const params = new URLSearchParams(location.search);
      const locationsParam = params.get('locations');
      const newParams = new URLSearchParams();
      locationsParam && newParams.set('locations', locationsParam);

      // NOTE: 利便性のため分譲と展示場で共通の建築エリア以外の検索条件も保持する。
      if (selectedTab === CategoryName.EXHIBITION) {
        navigate(`/exhibitions?${newParams.toString()}`);
      } else if (selectedTab === CategoryName.PROPERTY) {
        navigate(`/properties?${newParams.toString()}`);
      }
    },
    [onChangeTab, setIsSmaverse, location.search, navigate]
  );

  return (
    <Tabs
      value={selected}
      onChange={handleChange}
      variant="fullWidth"
      TabIndicatorProps={{
        style: {
          backgroundColor: theme.palette.primary.main,
        },
      }}
      sx={{
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        mx: 5,
        my: isMobile ? 1 : 5,
      }}
    >
      {tabs.map(({ label, value }, index) => (
        <Tab
          key={index}
          label={label}
          value={value}
          sx={{
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
            },
          }}
        />
      ))}
    </Tabs>
  );
}
